import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, SparklesIcon, ClockIcon } from '@heroicons/react/24/outline';

interface TrialReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  daysRemaining: number;
  totalArticlesRead: number;
  onExpressInterest: () => void;
}

const TrialReminderModal: React.FC<TrialReminderModalProps> = ({
  isOpen,
  onClose,
  daysRemaining,
  totalArticlesRead,
  onExpressInterest
}) => {
  if (!isOpen) return null;

  const getUrgencyMessage = () => {
    if (daysRemaining <= 2) {
      return {
        title: `Only ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left!`,
        subtitle: "Don't lose access to your learning journey",
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    } else if (daysRemaining <= 4) {
      return {
        title: `${daysRemaining} days remaining in your trial`,
        subtitle: "Time to make a decision about continuing",
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      };
    } else {
      return {
        title: `${daysRemaining} days left in your free trial`,
        subtitle: "Loving Knoozy? Let's keep learning together!",
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200'
      };
    }
  };

  const urgency = getUrgencyMessage();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header with gradient */}
          <div className={`${urgency.bgColor} ${urgency.borderColor} border-b p-6 relative`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/50 transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center space-x-3 mb-2">
              <ClockIcon className={`w-8 h-8 ${urgency.color}`} />
              <h2 className={`text-2xl font-bold ${urgency.color}`}>
                {urgency.title}
              </h2>
            </div>
            <p className="text-gray-600">{urgency.subtitle}</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Progress Stats */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Your Progress</span>
                <SparklesIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {totalArticlesRead} articles read
              </div>
              <p className="text-sm text-gray-600">
                You're on a great learning journey! Keep it going.
              </p>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">What you'll continue to enjoy:</h3>
              <ul className="space-y-2">
                {[
                  'Unlimited access to age-appropriate current affairs',
                  'Daily quizzes and interactive content',
                  'Progress tracking and achievements',
                  'Safe, ad-free learning environment'
                ].map((benefit, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-green-500 mt-0.5">✓</span>
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3 pt-4">
              <button
                onClick={onExpressInterest}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                I'm Interested in Subscribing
              </button>

              <button
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Remind Me Later
              </button>
            </div>

            <p className="text-xs text-center text-gray-500">
              {daysRemaining > 0
                ? `You'll be reminded again. Your trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}.`
                : "Your trial ends today. Choose a plan to continue learning!"}
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TrialReminderModal;
